import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { StateService } from '@uirouter/angular';
import { Subscription } from 'rxjs';
import * as _ from 'lodash';

import { WikiContainerControls } from '../../../widgets/wiki/container/wiki.container.component';
import { WikiService } from '../../../widgets/wiki/services/wiki.service';

/**
 * Project wiki page host — a thin wrapper that drops the self-contained `<wiki-container>` onto the
 * project wiki surface with the full tab set and page controls. All wiki behaviour (tabs, content,
 * save-on-leave, data loading) lives in the container; this component only supplies configuration,
 * plus the page's comments below it on the View tab.
 */
@Component({
    selector: 'wiki-page',
    template: `<wiki-container
        [baseState]="'home.project.summary.wiki.page'"
        [tabs]="['view','edit','versions','attachments','referrers','monitor']"
        [project]="project"
        [projectMenu]="projectMenu"
        [section]="'project'"
        [refId]="project?.id"
        [showSidebar]="true"
        [showTags]="true"
        [showAttachments]="true"
        [manageChrome]="true"
        [controls]="controls"></wiki-container>
    <div class="col-xs-12" *ngIf="wikiPage?.id && isViewTab">
        <div class="panel panel-default">
            <div class="panel-heading">
                <h4><span translate="">Comments</span>
                    <span class="badge" style="margin-left: .5em;" *ngIf="comments.count">{{comments.count}}</span></h4>
            </div>
            <wiki-comments #comments [wikiPage]="wikiPage" [project]="project"></wiki-comments>
        </div>
    </div>`
})
export class WikiPageComponent implements OnInit, OnDestroy {
    @Input() project: any = null;
    @Input() projectMenu: any = null;

    protected wikiPage: any = null;
    private wikiPageSub: Subscription | null = null;

    constructor(
        private stateService: StateService,
        private wikiService: WikiService,
    ) { }

    ngOnInit() {
        this.wikiPageSub = this.wikiService.wikiPage$.subscribe((wikiPage) => this.wikiPage = wikiPage);
    }

    ngOnDestroy() {
        if (this.wikiPageSub) {
            this.wikiPageSub.unsubscribe();
        }
    }

    get isViewTab(): boolean {
        return this.stateService.is('home.project.summary.wiki.page.view');
    }

    get controls(): WikiContainerControls {
        const canEdit = _.get(this.project, 'rel.permissions.wiki', 0) > 1;
        // Downloading is a read action: anyone who can see the page can export it, and
        // the endpoint authorises independently. So a read-only viewer keeps a toolbar
        // carrying nothing but the download — but only once there is a project, since
        // that is what scopes the export request.
        if (!canEdit) { return this.project ? { download: true } : {}; }
        const canAdminWiki = _.get(this.project, 'rel.permissions.admin', false)
            || _.get(this.project, 'rel.permissions.wiki', 0) >= 99;
        return {
            delete: canAdminWiki,
            lock: true,
            public: true,
            monitor: true,
            browse: true,
            download: true,
            browseState: 'home.project.summary.wiki.browse',
        };
    }
}
